import { useAuthStore } from '@/stores/auth'
import { Button } from '@/components/ui/button'
import { CreditCard, Trash2, Check, Smartphone, Wallet } from 'lucide-react'
import type { PaymentMethod } from '@/types/user'

export default function AccountPaymentMethodsPage() {
  const { paymentMethods, deletePaymentMethod, setDefaultPaymentMethod } = useAuthStore()

  const getPaymentIcon = (type: string) => {
    switch (type) {
      case 'card':
        return <CreditCard className="h-5 w-5" />
      case 'mobile_wallet':
        return <Smartphone className="h-5 w-5" />
      case 'bank':
        return <Wallet className="h-5 w-5" />
      default:
        return <CreditCard className="h-5 w-5" />
    }
  }

  const getPaymentLabel = (method: PaymentMethod) => {
    if (method.type === 'card' && method.last_four) {
      return `•••• •••• •••• ${method.last_four}`
    }
    if (method.type === 'mobile_wallet') {
      return `${method.provider}`
    }
    if (method.type === 'bank') {
      return `${method.provider}`
    }
    return method.provider
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-foreground">Payment Methods</h1>
        <Button>Add Payment Method</Button>
      </div>

      {paymentMethods.length === 0 ? (
        <div className="bg-card rounded-lg border border-border p-12 text-center">
          <CreditCard className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">
            No payment methods saved yet
          </h3>
          <p className="text-sm text-muted-foreground mb-4">
            Add your payment methods for faster checkout
          </p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {paymentMethods.map((method) => (
            <div
              key={method.id}
              className={`bg-card rounded-lg border ${
                method.is_default ? 'border-primary' : 'border-border'
              } p-6 relative`}
            >
              {method.is_default && (
                <span className="absolute top-4 right-4 inline-flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary text-xs font-medium rounded-full">
                  <Check className="h-3 w-3" />
                  Default
                </span>
              )}

              <div className="flex items-start gap-4 mb-4">
                <div className="p-3 bg-muted rounded-lg">
                  {getPaymentIcon(method.type)}
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-foreground mb-1 capitalize">
                    {method.type.replace('_', ' ')}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {getPaymentLabel(method)}
                  </p>
                  {method.holder_name && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {method.holder_name}
                    </p>
                  )}
                  {method.type === 'card' && method.expiry && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Expires: {method.expiry}
                    </p>
                  )}
                </div>
              </div>

              <div className="flex gap-2">
                {!method.is_default && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => setDefaultPaymentMethod(method.id)}
                  >
                    Set as Default
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => deletePaymentMethod(method.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="bg-muted/50 rounded-lg p-4">
        <p className="text-sm text-muted-foreground">
          💡 Your payment information is securely stored and encrypted. We never share your financial details with third parties.
        </p>
      </div>
    </div>
  )
}
